import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { employeesAPI } from '../lib/api';
import api from '../lib/api';
import { FileText, Upload, Info, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { TemplateCard } from './templates/TemplateCard';
import { UploadTemplateDialog } from './templates/UploadTemplateDialog';
import { GenerateTemplateDialog } from './templates/GenerateTemplateDialog';
import { GenerateListDialog } from './templates/GenerateListDialog';
import { EditTemplateDialog } from './templates/EditTemplateDialog';
import { PlaceholdersTab } from './templates/PlaceholdersTab';

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [placeholders, setPlaceholders] = useState({});
  const [listPlaceholders, setListPlaceholders] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('templates');

  // Upload Dialog
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    name: '',
    description: '',
    category: 'Allgemein',
    file: null,
  });

  // Generate Dialog
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [listDialogOpen, setListDialogOpen] = useState(false);
  const [listTemplate, setListTemplate] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [templatesRes, employeesRes, placeholdersRes] = await Promise.all([
        api.get('/templates'),
        employeesAPI.getAll({ is_active: true }),
        api.get('/templates/placeholders'),
      ]);
      setTemplates(templatesRes.data);
      setEmployees(employeesRes.data);
      setPlaceholders(placeholdersRes.data?.single || placeholdersRes.data || {});
      setListPlaceholders(placeholdersRes.data?.list || {});
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.name) {
      toast.error('Bitte Name und Datei angeben');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('name', uploadForm.name);
      formData.append('category', uploadForm.category);
      formData.append('description', uploadForm.description || '');

      await api.post('/templates', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: {
          name: uploadForm.name,
          category: uploadForm.category,
          description: uploadForm.description || '',
        },
      });

      toast.success('Vorlage hochgeladen');
      setUploadDialogOpen(false);
      setUploadForm({ name: '', description: '', category: 'Allgemein', file: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hochladen');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Möchten Sie diese Vorlage wirklich löschen?')) return;
    try {
      await api.delete(`/templates/${templateId}`);
      toast.success('Vorlage gelöscht');
      loadData();
    } catch {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleOpenInWord = async (template) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const downloadUrl = `${process.env.REACT_APP_BACKEND_URL}/api/templates/${template.id}/download`;
      const response = await fetch(downloadUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Download fehlgeschlagen');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${template.name}.${template.file_type}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Vorlage heruntergeladen - Öffnen Sie die Datei in Word/Excel zum Bearbeiten');
    } catch {
      toast.error('Fehler beim Herunterladen');
    }
  };

  const openGenerateDialog = (template) => {
    setSelectedTemplate(template);
    setSelectedEmployees([]);
    setGenerateDialogOpen(true);
  };

  const toggleEmployee = (empId) => {
    setSelectedEmployees((prev) =>
      prev.includes(empId) ? prev.filter((id) => id !== empId) : [...prev, empId]
    );
  };

  const selectAllEmployees = () => {
    if (selectedEmployees.length === employees.length) setSelectedEmployees([]);
    else setSelectedEmployees(employees.map((e) => e.id));
  };

  const handleGenerate = async (asPdf = false) => {
    if (!selectedTemplate || selectedEmployees.length === 0) {
      toast.error('Bitte mindestens einen Mitarbeiter auswählen');
      return;
    }
    // PDF-Konvertierung gibt es nur für Word-Vorlagen
    if (asPdf && selectedTemplate.file_type !== 'docx') {
      toast.error('PDF-Ausgabe ist nur für Word-Vorlagen möglich');
      return;
    }
    setGenerating(true);
    try {
      const response = await api.post(`/templates/${selectedTemplate.id}/generate`, {
        template_id: selectedTemplate.id,
        employee_ids: selectedEmployees,
        as_pdf: asPdf,
      });

      response.data.files.forEach((file) => {
        const byteCharacters = atob(file.content_base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const mime = file.file_type === 'pdf'
          ? 'application/pdf'
          : file.file_type === 'docx'
            ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        const blob = new Blob([byteArray], { type: mime });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      });

      toast.success(`${response.data.generated_count} Dokument(e) generiert`);
      setGenerateDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Generieren');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-[600px] rounded-xl" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="templates-page">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="font-heading font-bold text-3xl text-foreground">Dokumentvorlagen</h1>
            <p className="text-muted-foreground mt-1">
              Serienbriefe und Dokumente für Mitarbeiter generieren
            </p>
          </div>
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const res = await api.post('/templates/install-examples');
                const inst = res.data.installed_count;
                const skip = res.data.skipped_count;
                if (inst > 0 && skip > 0) toast.success(`${inst} Beispiel-Vorlage(n) installiert · ${skip} bereits vorhanden`);
                else if (inst > 0) toast.success(`${inst} Beispiel-Vorlage(n) installiert`);
                else toast.info('Alle Beispiel-Vorlagen sind bereits installiert');
                loadData();
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Installation fehlgeschlagen');
              }
            }}
            className="rounded-full"
            data-testid="install-examples-btn"
            title="Telefonliste, Anwesenheits-Liste und Notfall-Kontakt-Liste hinzufügen"
          >
            <Sparkles className="mr-2 h-4 w-4" />
            Beispiel-Vorlagen
          </Button>
          <Button
            onClick={() => setUploadDialogOpen(true)}
            className="rounded-full"
            data-testid="upload-template-btn"
          >
            <Upload className="mr-2 h-4 w-4" />
            Vorlage hochladen
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="templates" data-testid="tab-templates">
              <FileText className="h-4 w-4 mr-2" />
              Vorlagen ({templates.length})
            </TabsTrigger>
            <TabsTrigger value="placeholders" data-testid="tab-placeholders">
              <Info className="h-4 w-4 mr-2" />
              Platzhalter
            </TabsTrigger>
          </TabsList>

          <TabsContent value="templates" className="mt-6">
            {templates.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.map((template) => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    onGenerate={openGenerateDialog}
                    onGenerateList={(t) => { setListTemplate(t); setListDialogOpen(true); }}
                    onOpenInWord={handleOpenInWord}
                    onEdit={(t) => { setEditTemplate(t); setEditDialogOpen(true); }}
                    onDelete={handleDelete}
                    onRefresh={loadData}
                  />
                ))}
              </div>
            ) : (
              <Card className="border-border/60">
                <CardContent className="text-center py-12">
                  <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <h3 className="font-medium text-lg">Keine Vorlagen vorhanden</h3>
                  <p className="text-muted-foreground mt-1">
                    Laden Sie Ihre erste Word- oder Excel-Vorlage hoch.
                  </p>
                  <Button className="mt-4 rounded-full" onClick={() => setUploadDialogOpen(true)}>
                    <Upload className="mr-2 h-4 w-4" />
                    Vorlage hochladen
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="placeholders" className="mt-6">
            <PlaceholdersTab placeholders={placeholders} listPlaceholders={listPlaceholders} />
          </TabsContent>
        </Tabs>

        <UploadTemplateDialog
          open={uploadDialogOpen}
          onOpenChange={setUploadDialogOpen}
          uploadForm={uploadForm}
          setUploadForm={setUploadForm}
          onUpload={handleUpload}
          uploading={uploading}
        />

        <GenerateTemplateDialog
          open={generateDialogOpen}
          onOpenChange={setGenerateDialogOpen}
          selectedTemplate={selectedTemplate}
          employees={employees}
          selectedEmployees={selectedEmployees}
          toggleEmployee={toggleEmployee}
          selectAllEmployees={selectAllEmployees}
          onGenerate={handleGenerate}
          generating={generating}
        />

        <GenerateListDialog
          open={listDialogOpen}
          onOpenChange={setListDialogOpen}
          template={listTemplate}
        />

        <EditTemplateDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          template={editTemplate}
          onSaved={() => {
            loadData();
            setEditDialogOpen(false);
          }}
        />
      </div>
    </DashboardLayout>
  );
}
